import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { RefreshCw, Settings, ArrowRight, CheckCircle, Search } from 'lucide-react';

const Services = () => {
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const el = document.querySelector(hash);
      if (el) {
        // small delay so the page lays out before scrolling
        setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
      }
    } else {
      window.scrollTo({ top: 0, behavior: 'instant' in window ? 'instant' : 'auto' });
    }
  }, [hash]);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="gradient-bg pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Services
          </h1>
          <p className="text-xl text-white/90 max-w-3xl mx-auto">
            Professional bookkeeping and accounting services designed to help your business thrive
          </p>
        </div>
      </section>

      {/* How We Work Section */}
      <section id="how-we-work" className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-6" style={{ color: '#64B7B7' }}>
              How We Work
            </h2>
          </div>
          
          <div className="bg-white p-10 rounded-2xl shadow-lg">
            <p className="text-xl text-gray-700 mb-8 leading-relaxed">
              Most clients begin with a Financial Diagnostic Review to understand what is actually happening in the books before moving into cleanup, ongoing bookkeeping, or advisory services.
            </p>
            
            <p className="text-xl text-gray-700 mb-8 leading-relaxed">
              From there, services fall into two levels of support:
            </p>
            
            <div className="space-y-8 ml-4">
              <div>
                <h3 className="text-xl font-bold mb-2" style={{ color: '#B89A4E' }}>
                  Monthly Bookkeeping
                </h3>
                <p className="text-lg text-gray-600">
                  For accurate records and reporting.
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-bold mb-2" style={{ color: '#64B7B7' }}>
                  Monthly Bookkeeping + Financial Oversight
                </h3>
                <p className="text-lg text-gray-600">
                  For business owners who want guidance and ongoing support.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Financial Diagnostic Review */}
      <section id="financial-diagnostic-review" className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50" data-testid="financial-diagnostic-review-section">
        <div className="max-w-5xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl p-10 md:p-12 border-t-4" style={{ borderColor: '#B89A4E' }}>
            <span
              className="inline-block text-xs font-semibold tracking-widest uppercase px-3 py-1 rounded-full mb-5"
              style={{ backgroundColor: '#FAF4E4', color: '#B89A4E', border: '1px solid #B89A4E' }}
              data-testid="fdr-start-here-badge"
            >
              Most Clients Start Here
            </span>
            <div className="flex items-center mb-6">
              <div className="w-16 h-16 mr-5 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#B89A4E' }}>
                <Search className="w-8 h-8 text-white" />
              </div>
              <div>
                <h2 className="text-3xl md:text-4xl font-bold" style={{ color: '#64B7B7' }}>
                  Financial Diagnostic Review
                </h2>
                <p className="text-lg md:text-xl text-gray-600 mt-1">
                  Find What the Financials Are Actually Saying
                </p>
              </div>
            </div>

            <p className="text-lg text-gray-700 mb-10 leading-relaxed">
              Most financial problems have an underlying accounting or operational cause. The Financial Diagnostic Review is a technical review of the books designed to identify issues affecting profitability, cash flow, reporting accuracy, and financial structure.
            </p>

            <div className="grid md:grid-cols-2 gap-10 mb-10">
              <div>
                <h3 className="text-xl font-bold mb-4" style={{ color: '#B89A4E' }}>
                  Areas reviewed may include:
                </h3>
                <ul className="space-y-3">
                  {[
                    'Negative liabilities',
                    'Negative cash balances',
                    'Revenue recognition issues',
                    'Unreconciled accounts',
                    'Payroll liabilities',
                    'Misclassified transactions',
                    'Balance sheet inconsistencies',
                    'Cash flow concerns',
                    'Reporting irregularities',
                  ].map((item) => (
                    <li key={item} className="flex items-start">
                      <CheckCircle className="w-5 h-5 mr-3 mt-1 flex-shrink-0" style={{ color: '#78C4C4' }} />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-bold mb-4" style={{ color: '#B89A4E' }}>
                  What you receive:
                </h3>
                <ul className="space-y-3">
                  {[
                    'Technical financial review',
                    'Findings summary',
                    'Identified concerns',
                    'Recommended corrective actions',
                    'Suggested next steps',
                  ].map((item) => (
                    <li key={item} className="flex items-start">
                      <CheckCircle className="w-5 h-5 mr-3 mt-1 flex-shrink-0" style={{ color: '#78C4C4' }} />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <p className="text-lg text-gray-700 mb-8 leading-relaxed">
              This service is ideal for businesses that feel uncertain about their financial reporting, experienced a difficult tax season, need a second set of eyes on the books, or want to identify accounting issues before they grow.
            </p>

            <a
              href="https://calendly.com/joede1123"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-gold px-8 py-3 rounded-lg font-bold inline-flex items-center space-x-2"
              data-testid="financial-diagnostic-cta"
            >
              <span>Request a Review</span>
              <ArrowRight className="w-5 h-5" />
            </a>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Bookkeeping Cleanup */}
            <div className="bg-white rounded-2xl shadow-xl p-8 border-t-4 hover:shadow-2xl transition" style={{ borderColor: '#78C4C4' }}>
              <div className="w-16 h-16 mb-6 rounded-full flex items-center justify-center" style={{ backgroundColor: '#78C4C4' }}>
                <RefreshCw className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold mb-4" style={{ color: '#64B7B7' }}>
                Bookkeeping Cleanup
              </h3>
              <p className="text-gray-600 mb-6 text-lg leading-relaxed">
                Comprehensive cleanup services to organize past transactions, reconcile accounts, correct errors, and bring your books to a current and accurate position.
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 mr-3 mt-1 flex-shrink-0" style={{ color: '#78C4C4' }} />
                  <span className="text-gray-700">Historical transaction organization</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 mr-3 mt-1 flex-shrink-0" style={{ color: '#78C4C4' }} />
                  <span className="text-gray-700">Account reconciliation</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 mr-3 mt-1 flex-shrink-0" style={{ color: '#78C4C4' }} />
                  <span className="text-gray-700">Error correction and accuracy review</span>
                </li>
              </ul>
              <a 
                href="https://calendly.com/joede1123"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-gold px-6 py-3 rounded-lg font-bold inline-flex items-center space-x-2 w-full justify-center"
              >
                <span>Schedule Consultation</span>
                <ArrowRight className="w-5 h-5" />
              </a>
            </div>

            {/* Accounting Systems */}
            <div className="bg-white rounded-2xl shadow-xl p-8 border-t-4 hover:shadow-2xl transition" style={{ borderColor: '#64B7B7' }}>
              <div className="w-16 h-16 mb-6 rounded-full flex items-center justify-center" style={{ backgroundColor: '#64B7B7' }}>
                <Settings className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold mb-4" style={{ color: '#64B7B7' }}>
                Accounting Systems & Financial Structure
              </h3>
              <p className="text-gray-600 mb-6 text-lg leading-relaxed">
                Build or rebuild accounting systems that support your business, including chart of accounts structure, financial reporting, and sustainable bookkeeping processes.
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 mr-3 mt-1 flex-shrink-0" style={{ color: '#78C4C4' }} />
                  <span className="text-gray-700">Chart of accounts design</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 mr-3 mt-1 flex-shrink-0" style={{ color: '#78C4C4' }} />
                  <span className="text-gray-700">Financial reporting setup</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 mr-3 mt-1 flex-shrink-0" style={{ color: '#78C4C4' }} />
                  <span className="text-gray-700">System implementation support</span>
                </li>
              </ul>
              <a 
                href="https://calendly.com/joede1123"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-gold px-6 py-3 rounded-lg font-bold inline-flex items-center space-x-2 w-full justify-center"
              >
                <span>Schedule Consultation</span>
                <ArrowRight className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Advisory Services */}
      <section id="advisory-services" className="py-20 px-4 sm:px-6 lg:px-8 bg-white" data-testid="advisory-services-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12 max-w-3xl mx-auto">
            <span
              className="inline-block text-xs font-semibold tracking-widest uppercase px-3 py-1 rounded-full mb-5"
              style={{ backgroundColor: '#E6F2F2', color: '#64B7B7', border: '1px solid #64B7B7' }}
              data-testid="advisory-tag"
            >
              Advisory Support
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mb-6" style={{ color: '#64B7B7' }}>
              Business Guidance Backed by the Real Numbers in Your Business
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed">
              Advisory support at Brown &amp; Associates starts with reviewing your books and financial data. This is not generic business coaching or surface-level reporting. We help business owners understand what their numbers actually mean so they can make smarter business decisions throughout the year.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="bg-white rounded-2xl shadow-xl p-8 border-t-4" style={{ borderColor: '#B89A4E' }}>
              <h3 className="text-xl font-bold mb-4" style={{ color: '#64B7B7' }}>
                Financial Review &amp; Interpretation
              </h3>
              <p className="text-gray-700 leading-relaxed">
                Bookkeeping is not just transaction coding. We review revenue, expenses, liabilities, owner activity, payroll, and cash flow patterns to determine what the numbers are actually telling you.
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-xl p-8 border-t-4" style={{ borderColor: '#78C4C4' }}>
              <h3 className="text-xl font-bold mb-4" style={{ color: '#64B7B7' }}>
                Accounting Issue Support
              </h3>
              <p className="text-gray-700 leading-relaxed">
                Need help with a notice, entity election, payroll issue, audit support, deferred revenue, owner contributions, or questions about how activity should actually be treated? This is where that support lives.
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-xl p-8 border-t-4" style={{ borderColor: '#64B7B7' }}>
              <h3 className="text-xl font-bold mb-4" style={{ color: '#64B7B7' }}>
                Tax-Aware Decision Support
              </h3>
              <p className="text-gray-700 leading-relaxed">
                Business decisions can affect taxes, payroll, entity structure, deductions, and compliance. We help you evaluate those decisions before they create unnecessary cost or risk.
              </p>
            </div>
          </div>

          <div className="max-w-3xl mx-auto text-center">
            <p className="text-lg text-gray-700 mb-8 leading-relaxed">
              Recommendations are grounded in the actual financial structure of your business — not generic templates, assumptions, or one-size-fits-all advice.
            </p>
            <a
              href="https://calendly.com/joede1123"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-gold px-8 py-3 rounded-lg font-bold inline-flex items-center space-x-2"
              data-testid="advisory-services-cta"
            >
              <span>Schedule a Consultation</span>
              <ArrowRight className="w-5 h-5" />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
